import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, Phone } from "lucide-react";
import { BRAND } from "../mock/mock";
import logo from "../assets/meilleur-logo.png";

const collections = [
  { to: "/oud-chips", label: "Oud Chips" },
  { to: "/oud-oil", label: "Oud Oil" },
  { to: "/bakhoor", label: "Bakhoor" },
  { to: "/other-products", label: "Oud Craft" },
  { to: "/blend-finder", label: "Blend Finder" },
];

const Logo = () => (
  <Link to="/" className="flex items-center gap-3 leading-none group">
    <img
      src={logo}
      alt="Meilleur Oudh & Aromas logo"
      className="w-11 h-11 md:w-12 md:h-12 rounded-full object-cover shrink-0 ring-1 ring-[var(--line)] bg-white"
    />
    <span className="flex flex-col">
      <span className="font-marcellus text-xl md:text-2xl tracking-[0.18em] text-[var(--ivory)] group-hover:text-[var(--gold-light)] transition-colors">
        MEILLEUR
      </span>
      <span className="eyebrow mt-1 text-[0.55rem] tracking-[0.34em]">Oudh &amp; Aromas</span>
    </span>
  </Link>
);

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [colOpen, setColOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setColOpen(false);
  }, [location]);

  // Lock body scroll while the mobile drawer is open; always restore on close/unmount
  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prev || "";
      };
    }
    document.body.style.overflow = "";
  }, [open]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-[9999] transition-all duration-500 ${
        scrolled
          ? "bg-[rgba(245,240,230,0.92)] backdrop-blur-md border-b border-[var(--line)] py-4"
          : "bg-transparent py-7"
      }`}
    >
      <div className="max-w-[1360px] mx-auto px-6 md:px-10 flex items-center justify-between">
        <Logo />

        <nav className="hidden lg:flex items-center gap-10">
          <NavLink to="/" end className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}>Home</NavLink>
          <NavLink to="/about" className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}>About</NavLink>

          <div className="relative" onMouseEnter={() => setColOpen(true)} onMouseLeave={() => setColOpen(false)}>
            <button className="nav-link flex items-center gap-1.5">
              Collections <ChevronDown size={13} className={`transition-transform duration-300 ${colOpen ? "rotate-180" : ""}`} />
            </button>
            <div
              className={`absolute left-1/2 -translate-x-1/2 top-full pt-6 transition-all duration-300 ${
                colOpen ? "opacity-100 visible translate-y-0" : "opacity-0 invisible -translate-y-2"
              }`}
            >
              <div className="w-64 bg-[var(--noir-2)] border border-[var(--line)] p-2 shadow-2xl">
                {collections.map((c) => (
                  <Link
                    key={c.to}
                    to={c.to}
                    className="block px-5 py-3.5 text-sm tracking-[0.12em] uppercase text-[var(--ivory-muted)] hover:text-[var(--gold-light)] hover:bg-[rgba(20,81,60,0.07)] transition-colors"
                  >
                    {c.label}
                  </Link>
                ))}
              </div>
            </div>
          </div>

          <NavLink to="/house" className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}>The House</NavLink>
          <NavLink to="/gallery" className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}>Gallery</NavLink>
          <NavLink to="/recognitions" className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}>Recognitions</NavLink>
          <NavLink to="/contact" className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}>Contact</NavLink>
        </nav>

        <a href={`tel:${BRAND.phone.replace(/\s/g, "")}`} className="hidden lg:flex items-center gap-2 text-sm tracking-[0.14em] text-[var(--gold-light)] hover:text-[var(--gold)] transition-colors">
          <Phone size={15} /> {BRAND.phone}
        </a>

        <button className="lg:hidden text-[var(--green)]" onClick={() => setOpen(true)} aria-label="Menu">
          <Menu size={28} />
        </button>
      </div>

      {/* Mobile drawer */}
      <div className={`fixed inset-0 z-[10000] lg:hidden transition-all duration-500 ${open ? "visible" : "invisible"}`}>
        <div className={`absolute inset-0 bg-black/60 transition-opacity duration-500 ${open ? "opacity-100" : "opacity-0"}`} onClick={() => setOpen(false)} />
        <div className={`absolute right-0 top-0 h-full w-[86%] max-w-sm bg-[#f3ede0] border-l border-[var(--line)] p-8 overflow-y-auto transition-transform duration-500 ${open ? "translate-x-0" : "translate-x-full"}`}>
          <div className="flex justify-between items-center mb-10">
            <Logo />
            <button onClick={() => setOpen(false)} className="text-[var(--green)]" aria-label="Close menu"><X size={26} /></button>
          </div>
          <div className="flex flex-col gap-5">
            {[
              { to: "/", label: "Home" },
              { to: "/about", label: "About" },
              ...collections,
              { to: "/house", label: "The House" },
              { to: "/gallery", label: "Gallery" },
              { to: "/recognitions", label: "Recognitions" },
              { to: "/contact", label: "Contact" },
            ].map((l) => (
              <NavLink key={l.to} to={l.to} className="font-serif-display text-2xl text-[var(--ink)] hover:text-[var(--green)] transition-colors">
                {l.label}
              </NavLink>
            ))}
          </div>
          <a href={`tel:${BRAND.phone.replace(/\s/g, "")}`} className="mt-10 inline-flex items-center gap-2 text-[var(--green)]">
            <Phone size={16} /> {BRAND.phone}
          </a>
        </div>
      </div>
    </header>
  );
};

export default Navbar;
